import { Reducer } from "redux";

import FeatureFlag from "../FeatureFlag";
import SessionTimeoutManager from "../sessionTimeout/SessionTimeoutManager";
import { EnvironmentID, PropertySensitivityLevels } from "../types/global";

export const SET_ACCOUNT_METADATA = "SET_ACCOUNT_METADATA";
export const SET_CURRENT_ENVIRONMENT = "SET_CURRENT_ENVIRONMENT";

const LOCAL_STORE_CURRENT_ENV = "currentEnvironment";
const MOST_RECENT_ENV = "mostRecentEnv";

const SET_ACCOUNT_METADATA_UPDATING = "SET_ACCOUNT_METADATA_UPDATING";

export type AccountID = string;

export type ProjectID = string;

type ProjectCodeEval = {
  onPageLoad?: string;
  onPageChange?: string;
  onIdentify?: string;
  onTrack?: string;
};

type ProjectBase = {
  name: string;
  config: Record<string, boolean>;
  eventPropertySensitivityLevels: PropertySensitivityLevels;
  userPropertySensitivityLevels: PropertySensitivityLevels;
  groupPropertySensitivityLevels: PropertySensitivityLevels;
  accountID: string;
};

export type LocalStorageProject = ProjectBase & {
  ID: ProjectID;
};

type EnvironmentConfig = {
  consentManagementDetected?: boolean;
  videoCaptionsEnabledByDefault?: boolean;
};

type EnvironmentBase = {
  name: string;
  createdAt: string | null;
  apiHost?: string;
  appHost?: string;
  customDomain?: string;
  config?: EnvironmentConfig;
};

export type LocalStorageEnvironment = EnvironmentBase & {
  ID: EnvironmentID;
  project: LocalStorageProject;
};

export type EditableEnvironment = EnvironmentBase & {
  ID: EnvironmentID | null;
  accountID: AccountID | null;
  projectID: ProjectID | null;
  deletable: boolean;
};

export type EditableProject = ProjectBase & {
  ID: ProjectID | null;
  accountID: AccountID | null;
  environments: EditableEnvironment[];
  codeEval?: ProjectCodeEval;
};

export type AccountConfigNonStandardProperties = {
  api_host: string;
  app_host: string;
  sessionTimeoutMS?: number;
};

type AccountConfig = Partial<
  Record<FeatureFlag, boolean> & AccountConfigNonStandardProperties
>;

type CompanyDetails = {
  name: string;
};

export type AccountMetadata = {
  accountID: AccountID | null;
  config: AccountConfig;
  projects: Array<EditableProject>;
  companyDetails: CompanyDetails;
  adblockCloudfrontID?: string;
  customDomain?: string;
};

export class JSONValueStorage<T> {
  storage: Storage;
  key: string;

  constructor(location: Storage, key: string) {
    this.storage = location;
    this.key = key;
  }

  set(objValue: T | null) {
    this.storage.setItem(this.key, JSON.stringify(objValue));
  }

  get(): T | null {
    const val = this.storage.getItem(this.key);
    if (!val) {
      return null;
    }

    try {
      return JSON.parse(val);
    } catch {
      return null;
    }
  }
}

export class FPEnvironmentLocker {
  currentEnvStorage: JSONValueStorage<string>;
  mostRecentEnvStorage: JSONValueStorage<string>;
  accountMetadata: AccountMetadata | null;

  constructor() {
    // We keep the current env in sessionStorage, but keep track of the most recently
    // used env in localStorage to use when a tab first loads.
    this.currentEnvStorage = new JSONValueStorage(
      window.sessionStorage,
      LOCAL_STORE_CURRENT_ENV,
    );
    this.mostRecentEnvStorage = new JSONValueStorage(
      window.localStorage,
      MOST_RECENT_ENV,
    );
    this.accountMetadata = null;
  }

  setCurrentEnv(currentEnvID: string) {
    this.setMostRecentEnv(currentEnvID);
    this.currentEnvStorage.set(currentEnvID);
  }

  setMostRecentEnv(currentEnvID: string) {
    this.mostRecentEnvStorage.set(currentEnvID);
  }

  getCurrentEnvID(): string | null {
    return this.currentEnvStorage.get();
  }

  getRecentEnvID(): string | null {
    return this.mostRecentEnvStorage.get();
  }

  getEnvByID(envID: string | null): LocalStorageEnvironment | null {
    if (envID && this.accountMetadata) {
      for (let project of this.accountMetadata.projects) {
        for (let env of project.environments) {
          if (env.ID === envID) {
            return {
              ...env,
              // @ts-ignore
              project: project,
            };
          }
        }
      }
    }

    return null;
  }

  getDefaultEnv(): LocalStorageEnvironment | null {
    // Just get the first env in the first project
    if (
      this.accountMetadata &&
      this.accountMetadata.projects &&
      this.accountMetadata.projects.length &&
      this.accountMetadata.projects[0] &&
      this.accountMetadata.projects[0].environments &&
      this.accountMetadata.projects[0].environments.length
    ) {
      const proj = this.accountMetadata.projects[0];
      const env = proj.environments[0];
      return {
        ...env,
        // @ts-ignore
        project: proj,
      };
    }
    return null;
  }

  /** Gets the env ID to use on the page. In order try:
   * - Current Env (sessionStorage)
   * - Most recently used Env (localStorage)
   * - Default Env
   */
  getEnvID(): string | null {
    const defaultEnv = this.getDefaultEnv();
    return (
      this.getCurrentEnvID() ||
      this.getRecentEnvID() ||
      (defaultEnv && defaultEnv.ID)
    );
  }

  /** Gets the environment to use on the page.    */
  getCurrentEnv(): LocalStorageEnvironment | null {
    return this.getEnvByID(this.getEnvID());
  }

  initialize(accountMetadata: AccountMetadata) {
    this.accountMetadata = accountMetadata;

    if (this.getEnvByID(this.getCurrentEnvID())) {
      return;
    }

    // If selecting the recent env, we now need to set this env as the current env.
    const recentEnv = this.getEnvByID(this.getRecentEnvID());
    if (recentEnv) {
      return this.setCurrentEnv(recentEnv.ID);
    }

    // If selecting the default env, we now need to set this env as the current env.
    const defaultEnv = this.getDefaultEnv();
    if (defaultEnv) {
      this.setCurrentEnv(defaultEnv.ID);
    }
  }
}

export const envLocker = new FPEnvironmentLocker();

export type EnvironmentsState = {
  accountMetadata: AccountMetadata;
  loaded: boolean;
  currentEnvironment: LocalStorageEnvironment | null; // This allows us to always have the ability to get the currentEnv directly from redux
};

export const initialEnvironmentsState: EnvironmentsState = {
  accountMetadata: {
    accountID: null,
    // Default HIPAA to true until we load the actual config. Do this so if we
    // depend on the HIPAA flag, we always err on the safer side.
    config: {
      hipaaV2: true,
      sqlTransformations: true,
    },
    projects: [],
    companyDetails: {
      name: "",
    },
  },
  loaded: false,
  currentEnvironment: null,
};

// Actions
type SetAccountMetadataAction = {
  type: typeof SET_ACCOUNT_METADATA;
  payload: AccountMetadata;
};

type SetCurrentEnvironmentAction = {
  type: typeof SET_CURRENT_ENVIRONMENT;
  payload: string;
};

type SetAccountMetadataSavingAction = {
  type: typeof SET_ACCOUNT_METADATA_UPDATING;
};

export type EnvironmentActions =
  | SetAccountMetadataAction
  | SetCurrentEnvironmentAction
  | SetAccountMetadataSavingAction;

const environmentsReducer: Reducer<EnvironmentsState, EnvironmentActions> = (
  state = initialEnvironmentsState,
  action,
): EnvironmentsState => {
  switch (action.type) {
    case SET_ACCOUNT_METADATA:
      envLocker.initialize(action.payload);

      SessionTimeoutManager.initialize(action.payload.config.sessionTimeoutMS);

      return {
        ...state,
        accountMetadata: {
          ...action.payload,
        },
        loaded: true,
        currentEnvironment: envLocker.getCurrentEnv(),
      };
    case SET_CURRENT_ENVIRONMENT:
      envLocker.setCurrentEnv(action.payload);
      return {
        ...state,
        currentEnvironment: envLocker.getCurrentEnv(),
      };
    case SET_ACCOUNT_METADATA_UPDATING:
      return {
        ...state,
        loaded: false,
      };
    default:
      return state;
  }
};

export default environmentsReducer;
